import { Typography, Divider, Stack } from '@mui/material';

export default function TermsOfUseEn() {
  return (
    <Stack spacing={3}>
      <Typography variant="h4" fontWeight={700}>
        BRITESTAGE – General User Terms of Use
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <strong>Version 1.0</strong>
        <br />
        Last updated: 13th April 2026
      </Typography>

      <Typography variant="body1">
        These General User Terms of Use (&quot;Terms&quot;) govern the contractual relationship between:
      </Typography>
      <Typography variant="body1">
        <strong>21Minds GmbH</strong>
        <br />
        Botnanger Str. 6
        <br />
        70193 Stuttgart
        <br />
        Germany
        <br />
        (&quot;BRITESTAGE&quot;, &quot;we&quot;, &quot;us&quot;)
      </Typography>
      <Typography variant="body1">and</Typography>
      <Typography variant="body1">
        any natural person acting in a professional or commercial capacity (&quot;User&quot;, &quot;you&quot;).
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        1. Scope and Nature of the Platform
      </Typography>
      <Typography variant="body1">
        1.1 BRITESTAGE is a professional event infrastructure and collaboration platform enabling planning, management,
        presentation and promotion of events and related content.
      </Typography>
      <Typography variant="body1">
        1.2 The platform may include profiles, collaboration tools, contributor management, marketplace features,
        AI-assisted tools and paid services.
      </Typography>
      <Typography variant="body1">
        1.3 The platform is intended exclusively for entrepreneurs within the meaning of § 14 BGB and comparable
        international provisions.
      </Typography>
      <Typography variant="body1">
        1.4 If a User qualifies as a consumer under applicable law, mandatory consumer protection provisions shall apply
        in addition to these Terms.
      </Typography>
      <Typography variant="body1">1.5 Separate Organization Terms may apply.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        2. Eligibility and Account Registration
      </Typography>
      <Typography variant="body1">2.1 Only legally competent persons may register.</Typography>
      <Typography variant="body1">2.2 Users must provide accurate, complete and up-to-date information.</Typography>
      <Typography variant="body1">2.3 Accounts are personal and non-transferable.</Typography>
      <Typography variant="body1">
        2.4 Users are responsible for safeguarding credentials and all activities under their account.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        3. User-Centric Account Model
      </Typography>
      <Typography variant="body1">3.1 Users maintain independent accounts.</Typography>
      <Typography variant="body1">3.2 Users may interact with multiple organizations and events.</Typography>
      <Typography variant="body1">
        3.3 Deletion of accounts does not automatically remove event-integrated contributions.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        4. Use on Behalf of Organizations
      </Typography>
      <Typography variant="body1">4.1 Users may act on behalf of organizations within the platform.</Typography>
      <Typography variant="body1">
        4.2 In such cases, Users act as authorized representatives of the respective organization.
      </Typography>
      <Typography variant="body1">4.3 The organization is solely responsible for:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>defining access rights and permissions;</li>
          <li>ensuring lawful processing of personal data;</li>
          <li>compliance with applicable legal obligations.</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        4.4 The relationship between BRITESTAGE and the organization may be governed by separate Organization Terms and,
        where applicable, a Data Processing Agreement (DPA).
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        5. Profile Visibility
      </Typography>
      <Typography variant="body1">5.1 Visibility is configurable and opt-in.</Typography>
      <Typography variant="body1">5.2 Users are solely responsible for all published information.</Typography>
      <Typography variant="body1">5.3 BRITESTAGE does not verify the accuracy of profile data.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        6. Ratings and Rankings
      </Typography>
      <Typography variant="body1">6.1 Ratings reflect user opinions and are not endorsed by BRITESTAGE.</Typography>
      <Typography variant="body1">6.2 BRITESTAGE does not verify ratings for accuracy.</Typography>
      <Typography variant="body1">
        6.3 No guarantee is given regarding ranking logic, visibility or fairness.
      </Typography>
      <Typography variant="body1">6.4 Manipulation of ratings or rankings is prohibited.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        7. Marketplace and Third-Party Contracts
      </Typography>
      <Typography variant="body1">7.1 BRITESTAGE provides a technical infrastructure only.</Typography>
      <Typography variant="body1">7.2 Contracts are concluded exclusively between Users and third parties.</Typography>
      <Typography variant="body1">
        7.3 BRITESTAGE is neither agent, broker, intermediary, employer nor representative.
      </Typography>
      <Typography variant="body1">
        7.4 BRITESTAGE does not verify the identity, qualifications, legality or reliability of Users or organizations.
      </Typography>
      <Typography variant="body1">
        7.5 BRITESTAGE does not guarantee performance, execution or outcomes of third-party services.
      </Typography>
      <Typography variant="body1">7.6 BRITESTAGE is not involved in disputes between Users.</Typography>
      <Typography variant="body1">7.7 Payment processing may be handled by third parties.</Typography>
      <Typography variant="body1">
        7.8 Users are solely responsible for tax, legal and regulatory obligations.
      </Typography>
      <Typography variant="body1">
        7.9 Users acknowledge that any exchange of personal data between Users and organizations in the context of
        marketplace interactions takes place under their own responsibility as independent controllers.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        8. Paid Features and Subscriptions
      </Typography>
      <Typography variant="body1">8.1 Certain features require payment.</Typography>
      <Typography variant="body1">8.2 Subscriptions renew automatically unless terminated.</Typography>
      <Typography variant="body1">8.3 To the maximum extent permitted by law, payments are non-refundable.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        9. AI-Assisted Features
      </Typography>
      <Typography variant="body1">9.1 AI features are assistive tools only.</Typography>
      <Typography variant="body1">
        9.2 No guarantee is given regarding accuracy, completeness, reliability or suitability.
      </Typography>
      <Typography variant="body1">9.3 AI outputs may be incomplete, biased or incorrect.</Typography>
      <Typography variant="body1">9.4 Users remain fully responsible for all decisions.</Typography>
      <Typography variant="body1">9.5 AI outputs do not constitute legal, financial or professional advice.</Typography>
      <Typography variant="body1">9.6 BRITESTAGE shall not be liable for AI-generated outputs.</Typography>
      <Typography variant="body1">
        9.7 To the extent AI-assisted features involve the processing of personal data, such processing is carried out
        in accordance with the Privacy Policy and, where applicable, under the responsibility of the respective
        organization acting as controller.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        10. Acceptable Use
      </Typography>
      <Typography variant="body1">Users must not:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>Violate applicable laws</li>
          <li>Infringe third-party rights</li>
          <li>Upload unlawful or harmful content</li>
          <li>Manipulate systems or rankings</li>
          <li>Circumvent fees or commissions</li>
          <li>Access systems without authorization</li>
          <li>Interfere with platform security</li>
        </ul>
      </Typography>
      <Typography variant="body1">BRITESTAGE may suspend or restrict accounts in case of violations.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        11. Digital Services Act Compliance
      </Typography>
      <Typography variant="body1">11.1 Reporting mechanisms for unlawful content are provided.</Typography>
      <Typography variant="body1">11.2 BRITESTAGE has no general obligation to monitor content.</Typography>
      <Typography variant="body1">11.3 Content may be removed or restricted in good faith.</Typography>
      <Typography variant="body1">11.4 Repeat violations may lead to suspension.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        12. Data Protection and Roles
      </Typography>
      <Typography variant="body1">
        12.1 BRITESTAGE processes personal data in accordance with applicable data protection laws, in particular the
        General Data Protection Regulation (GDPR).
      </Typography>
      <Typography variant="body1">12.2 BRITESTAGE acts as an independent controller where Users:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>create and manage personal profiles;</li>
          <li>use marketplace features;</li>
          <li>interact with ratings, rankings and public-facing features.</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        12.3 Where Users act on behalf of an organization and process personal data within events, sessions or
        organizational structures, BRITESTAGE acts as a processor on behalf of the respective organization.
      </Typography>
      <Typography variant="body1">
        12.4 In such cases, a Data Processing Agreement (DPA) forms an integral part of the contractual relationship
        between BRITESTAGE and the respective organization.
      </Typography>
      <Typography variant="body1">
        12.5 The scope, purpose and legal basis of data processing are further defined in the Privacy Policy and, where
        applicable, in the DPA.
      </Typography>
      <Typography variant="body1">
        12.6 Users warrant that they are authorized to process personal data within the platform and, where applicable,
        have obtained all necessary consents or legal bases.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        13. Intellectual Property
      </Typography>
      <Typography variant="body1">13.1 Users retain ownership of their content.</Typography>
      <Typography variant="body1">
        13.2 Users grant BRITESTAGE a worldwide, non-exclusive, royalty-free, transferable and sublicensable license to
        use content strictly to the extent necessary for:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>operation and provision of the platform;</li>
          <li>technical processing, storage and transmission;</li>
          <li>display and distribution within the platform;</li>
          <li>security, debugging and system improvement;</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        13.3 Such use shall be limited to what is necessary for the performance of the contract and BRITESTAGE&apos;s
        legitimate interests in operating and improving the platform.
      </Typography>
      <Typography variant="body1">
        13.4 BRITESTAGE may use anonymized or aggregated data for analytics, service improvement and product
        development, provided that such data cannot be attributed to an identified or identifiable individual.
      </Typography>
      <Typography variant="body1">
        13.5 Event organizers may receive necessary rights to use contributions within the event context.
      </Typography>
      <Typography variant="body1">13.6 Users warrant that they hold all required rights.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        14. Third-Party Services
      </Typography>
      <Typography variant="body1">14.1 BRITESTAGE uses third-party providers.</Typography>
      <Typography variant="body1">
        14.2 BRITESTAGE is not responsible for their availability, performance or compliance.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        15. Data Retention and Deletion
      </Typography>
      <Typography variant="body1">15.1 Accounts may be deleted at any time.</Typography>
      <Typography variant="body1">15.2 Data is deleted unless legal retention obligations apply.</Typography>
      <Typography variant="body1">
        15.3 Contributions may remain in anonymized or embedded form within event contexts.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        16. Suspension and Termination
      </Typography>
      <Typography variant="body1">16.1 Accounts may be suspended or terminated for violations.</Typography>
      <Typography variant="body1">16.2 Immediate suspension is permitted where necessary.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        17. Service Availability and Data Integrity
      </Typography>
      <Typography variant="body1">
        17.1 BRITESTAGE provides the platform with a commercially reasonable level of care and technical diligence.
        However, uninterrupted, timely, secure or error-free availability of the platform is not guaranteed.
      </Typography>
      <Typography variant="body1">17.2 In particular, BRITESTAGE does not guarantee that:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>the platform will be available at all times or without interruption;</li>
          <li>access will be free from delays, outages or technical disruptions;</li>
          <li>the platform will operate without errors or defects;</li>
          <li>data will be stored or transmitted without loss or corruption.</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        17.3 Temporary restrictions or interruptions may occur, in particular due to:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>maintenance, updates or technical improvements;</li>
          <li>capacity limitations or load peaks;</li>
          <li>failures of third-party systems or infrastructure;</li>
          <li>events beyond BRITESTAGE&apos;s control (including force majeure).</li>
        </ul>
      </Typography>
      <Typography variant="body1">
        17.4 BRITESTAGE shall use commercially reasonable efforts to minimize disruptions and restore availability as
        quickly as possible.
      </Typography>
      <Typography variant="body1">
        17.5 Users are solely responsible for independently backing up their data and content at appropriate intervals.
      </Typography>
      <Typography variant="body1">
        17.6 No guarantees are given regarding specific service levels, uptime percentages or recovery times unless
        expressly agreed in a separate written Service Level Agreement (SLA).
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        18. Force Majeure
      </Typography>
      <Typography variant="body1">BRITESTAGE shall not be liable for events beyond its reasonable control.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        19. Limitation of Liability
      </Typography>
      <Typography variant="body1">
        19.1 Unlimited liability applies in cases of intent, gross negligence, injury to life, body or health, and
        mandatory statutory liability.
      </Typography>
      <Typography variant="body1">
        19.2 In cases of simple negligence, liability is limited to breaches of essential contractual obligations
        (Kardinalpflichten).
      </Typography>
      <Typography variant="body1">19.3 In such cases, liability is limited to foreseeable, typical damages.</Typography>
      <Typography variant="body1">
        19.4 Liability for indirect damages, lost profits, data loss, marketplace interactions, third-party services and
        AI outputs is excluded to the extent permitted by law.
      </Typography>
      <Typography variant="body1">19.5 Total liability is limited to the higher of:</Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>fees paid in the last 12 months, or</li>
          <li>EUR 10,000</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        20. Indemnification
      </Typography>
      <Typography variant="body1">
        Users shall indemnify and hold harmless BRITESTAGE against third-party claims arising from:
      </Typography>
      <Typography variant="body1" component="div">
        <ul>
          <li>unlawful content</li>
          <li>intellectual property violations</li>
          <li>misuse of the platform</li>
          <li>violations of these Terms</li>
        </ul>
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        21. Export Control and Sanctions
      </Typography>
      <Typography variant="body1">
        Users must comply with all applicable export control and sanctions laws and must not use the platform in
        restricted jurisdictions or for prohibited purposes.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        22. Amendments
      </Typography>
      <Typography variant="body1">22.1 BRITESTAGE may amend these Terms with reasonable notice.</Typography>
      <Typography variant="body1">22.2 Continued use constitutes acceptance.</Typography>
      <Typography variant="body1">22.3 Users may terminate before changes take effect.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        23. Governing Law and Jurisdiction
      </Typography>
      <Typography variant="body1">23.1 German law applies excluding CISG.</Typography>
      <Typography variant="body1">
        23.2 Exclusive jurisdiction is Stuttgart, Germany, where legally permissible.
      </Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        24. Language
      </Typography>
      <Typography variant="body1">The English version prevails.</Typography>

      <Divider />

      <Typography variant="h5" fontWeight={600}>
        25. Final Provisions
      </Typography>
      <Typography variant="body1">25.1 If any provision is invalid, the remainder remains unaffected.</Typography>
      <Typography variant="body1">25.2 No waiver applies unless explicitly declared.</Typography>
    </Stack>
  );
}
